/**
 * 中継サーバーとの通信層。
 *
 * 仕様は docs/02（GVConnector.exe の逆アセンブル解析＋実サーバーでの実測）。
 * `gotpos` がバイトオフセットであることは実測で確認済み。
 */

export const SUBMIT_TOKEN = 'nishimura';
export const DEFAULT_POLLING_MS = 10000;

/**
 * バイト列を1バイト＝1文字の文字列にする（latin1 相当）。
 *
 * ★ `TextDecoder('latin1')` は使えない。Encoding Standard では `latin1` も
 *   `iso-8859-1` も **windows-1252 の別名**と定められており、0x80〜0x9f が
 *   別の文字に化ける（例: 0x80 → U+20AC）。ここが欲しいのはバイト透過なので、
 *   コードポイントをそのまま写す必要がある。
 *
 * `String.fromCharCode(...bytes)` を一度に呼ぶと引数が多すぎてスタックを
 * 溢れさせるため、分割して連結する。
 */
export function bytesToLatin1(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  if (bytes.length <= CHUNK) return String.fromCharCode(...bytes);
  let out = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    out += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return out;
}

const isUnreserved = (c: number) =>
  (c >= 0x30 && c <= 0x39) || (c >= 0x41 && c <= 0x5a) || (c >= 0x61 && c <= 0x7a);

/**
 * GVConnector 独自の URL エンコード（`0x401450`）。
 * 空白は `+`、英数字はそのまま、それ以外はすべて**小文字**の %xx。
 * `-` `_` `.` `~` もエスケープされる点が encodeURIComponent と異なる。
 */
export function urlEncodeGV(text: string): string {
  let out = '';
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i) & 0xff;
    if (c === 0x20) out += '+';
    else if (isUnreserved(c)) out += text[i];
    else out += '%' + c.toString(16).padStart(2, '0');
  }
  return out;
}

/** POST ボディを組み立てる */
export function buildRequestBody(gotpos: number, data: string): string {
  return `gotpos=${gotpos}&data=${urlEncodeGV(data)}&submit=${SUBMIT_TOKEN}`;
}

const hexValue = (c: number): number => {
  if (c >= 0x30 && c <= 0x39) return c - 0x30;
  if (c >= 0x41 && c <= 0x46) return c - 0x41 + 10;
  if (c >= 0x61 && c <= 0x66) return c - 0x61 + 10;
  return -1;
};

/**
 * `urlEncodeGV` の逆。
 * 値はバイト透過で扱う（UTF-8 として解釈すると NMEA 以外の混入で壊れるため）。
 */
export function urlDecodeGV(text: string): string {
  let out = '';
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c === 0x2b) {
      out += ' ';
    } else if (c === 0x25 && i + 2 < text.length) {
      const hi = hexValue(text.charCodeAt(i + 1));
      const lo = hexValue(text.charCodeAt(i + 2));
      if (hi >= 0 && lo >= 0) {
        out += String.fromCharCode((hi << 4) | lo);
        i += 2;
      } else {
        out += text[i];
      }
    } else {
      out += text[i];
    }
  }
  return out;
}

export interface RelayRequest {
  gotpos: number;
  data: string;
  submit: string;
}

/**
 * POST ボディ `gotpos=N&data=...&submit=nishimura` を解析する。
 * サーバー側（ダミーサーバー・GV Bridge）で使う。
 */
export function parseRequestBody(body: string | Uint8Array): RelayRequest {
  const text = typeof body === 'string' ? body : bytesToLatin1(body);

  const fields = new Map<string, string>();
  for (const pair of text.split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    const key = eq < 0 ? pair : pair.slice(0, eq);
    fields.set(urlDecodeGV(key), eq < 0 ? '' : pair.slice(eq + 1));
  }

  // atoi 相当。読めなければ 0
  const gotpos = Number.parseInt(urlDecodeGV(fields.get('gotpos') ?? ''), 10);
  return {
    gotpos: Number.isFinite(gotpos) ? gotpos : 0,
    data: urlDecodeGV(fields.get('data') ?? ''),
    submit: urlDecodeGV(fields.get('submit') ?? ''),
  };
}

/**
 * レスポンスボディ `<データ長>\n<新しいgotpos>\n<データ本体>` を組み立てる。
 * データ長は本体の**バイト数**。文字列は latin1 相当で扱うので `.length` と一致する。
 */
export function buildResponseBody(chunk: string, nextPos: number): string {
  return `${chunk.length}\n${nextPos}\n${chunk}`;
}

export interface ServerResponse {
  /** データ本体のバイト数（サーバー申告値） */
  length: number;
  /** 次回送るカーソル */
  gotpos: number;
  /** データ本体 */
  data: string;
}

/** レスポンスボディ `<長さ>\n<新gotpos>\n<本体>` を解析する */
export function parseResponseBody(body: string): ServerResponse {
  const nl1 = body.indexOf('\n');
  if (nl1 < 0) throw new Error('レスポンスに1つ目の改行がありません');
  const nl2 = body.indexOf('\n', nl1 + 1);
  if (nl2 < 0) throw new Error('レスポンスに2つ目の改行がありません');
  const length = Number.parseInt(body.slice(0, nl1), 10) || 0;
  const gotpos = Number.parseInt(body.slice(nl1 + 1, nl2), 10) || 0;
  return { length, gotpos, data: body.slice(nl2 + 1, nl2 + 1 + length) };
}

export interface RelayClientOptions {
  /** 例: http://balloon.matrix.jp/cgi-bin/sr.cgi */
  serverUrl: string;
  /** ポーリング間隔 [ms]。既定10秒は実機と同じ */
  pollingMs?: number;
  /** 差し替え可能な fetch。テストや Node/ブラウザの差異を吸収する */
  fetchImpl?: typeof fetch;
}

/**
 * 中継サーバーのポーリングクライアント。
 *
 * ★ 送信バッファは **HTTP 200 が返ったときだけ**クリアする。
 *   失敗時は保持したままなので、次回ポーリングで自動再送される。
 *   これは GVConnector の設計（`0x401a81`）をそのまま踏襲したもの。
 */
export class RelayClient {
  #url: string;
  #pollingMs: number;
  #fetch: typeof fetch;
  #outbox: string[] = [];
  #timer: ReturnType<typeof setInterval> | null = null;

  /** サーバー側カーソル。0 なら全履歴を取得する */
  gotpos = 0;

  /** 受信データのハンドラ */
  onData: ((data: string) => void) | null = null;
  /** エラーハンドラ */
  onError: ((error: unknown) => void) | null = null;
  /** ポーリングが HTTP 200 で成功したときのハンドラ（データの有無を問わない） */
  onPollSuccess: (() => void) | null = null;

  constructor(options: RelayClientOptions) {
    this.#url = options.serverUrl;
    this.#pollingMs = options.pollingMs ?? DEFAULT_POLLING_MS;
    // ブラウザの fetch は Window に束縛されている。そのまま持ち回ると
    // `this` を失って "Illegal invocation" になるので、必ず束縛しておく。
    this.#fetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /** 送信キューに積む。実際の送出は次のポーリングでまとめて行われる */
  send(line: string): void {
    this.#outbox.push(line);
  }

  /** 1回だけ送受信する */
  async poll(): Promise<ServerResponse | null> {
    // ★ 送った分の「件数」を覚えておき、あとで送った分だけを取り除く。
    //   `await fetch(...)` の間にも `send()` は呼ばれうる（GPS の
    //   watchPosition や手動送信のタイマーなど、外部からの非同期呼び出し）。
    //   応答が返ってきたときに `#outbox` を単純に空にすると、await 中に
    //   新しく積まれた行まで一緒に消えてしまい、二度と送られないまま
    //   消失する（実測で発生: 4回に1回もサーバーに届かなかった）。
    const pending = this.#outbox.length;
    const payload = this.#outbox.slice(0, pending).join('');
    const body = buildRequestBody(this.gotpos, payload);

    const res = await this.#fetch(this.#url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });

    // 200 以外なら送信バッファを保持したまま次回へ回す
    if (res.status !== 200) {
      throw new Error(`中継サーバーが HTTP ${res.status} を返しました`);
    }

    // 送信できたぶんだけ取り除く。await中に追加された分は残す
    this.#outbox.splice(0, pending);

    // ★ `res.text()` は使わない。Content-Type に関わらず**常に UTF-8 で
    //   デコード**する仕様なので、0x80 以上のバイトが1つでも混ざると置換文字
    //   （U+FFFD）になり、`parseResponseBody` が使う**バイト数**としての
    //   `length` と実際の文字数がずれて以降が丸ごと壊れる。
    //   ストリームは他ツール（TV など）と共用でバイト透過が前提（docs/03 §5）。
    const parsed = parseResponseBody(bytesToLatin1(new Uint8Array(await res.arrayBuffer())));
    this.gotpos = parsed.gotpos;
    this.onPollSuccess?.();
    if (parsed.data.length > 0) this.onData?.(parsed.data);
    return parsed;
  }

  start(): void {
    if (this.#timer) return;
    const tick = () => {
      this.poll().catch((e) => this.onError?.(e));
    };
    this.#timer = setInterval(tick, this.#pollingMs);
    tick();
  }

  stop(): void {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
  }

  /** 送信待ちの行数 */
  get pendingCount(): number {
    return this.#outbox.length;
  }
}
